/**
 * Region skill fit — "how will this region go for me, at this tier?"
 *
 * The raid planner used to answer this with a three-column wall of every skill
 * in the region, its level band, a tick, and a success percentage. Accurate, and
 * far too tall: on a 768px iPad viewport it pushed the region tiles and the
 * Generate button below the fold. The detail is still the right answer once a
 * player is CHOOSING A PATH — it just is not the right answer while they are
 * choosing a region, where the only question is "can I handle this place?"
 *
 * So this collapses the same inputs to one verdict per region, and the tiles
 * carry a single glyph.
 *
 * Headless and separate from the renderer on purpose: the thresholds ARE the
 * design, and burying them in a template string is how they end up disagreeing
 * with the detailed view that uses the same numbers.
 */

import * as config from './../constants.js';
import { gameState } from './gameState-core.js';
import { getSkillCheckChance } from './eventSystem-core.js';
import { getSkillArtifactBonus } from './character-core.js';
import { getSkillFaction } from './util.js';

/**
 * Rooms a path visits, matching the income model in
 * `tools/loot-equilibrium.mjs` and `demand-core.js`. Hazards outnumber gates
 * 2:1, so a region's difficulty is set more by its hazards — weighting them
 * evenly would flatter a region with brutal hazards behind easy gates.
 */
const GATES_PER_RAID = 2;
const HAZARDS_PER_RAID = 4;

/**
 * Verdict bands, on the composite fit score (0..1) — NOT a success chance.
 *
 * The score blends two things measured on the same 0..1 scale but meaning
 * different things (see regionSkillFit), so it is a ranking, not a probability,
 * and calling it a percentage in the UI would be a lie. What anchors it is the
 * level half: 0.76 there is exactly `getSkillCheckChance` at target level, so a
 * character at the level a tier is built for, in their faction's best region,
 * lands just at GOOD.
 */
export const FIT_GOOD = 0.75;
export const FIT_CAUTION = 0.5;

export const FIT_DISPLAY = {
    good:    { glyph: '✅', label: 'Well matched', color: '#4ade80' },
    caution: { glyph: '⚠️', label: 'Risky',        color: '#fbbf24' },
    poor:    { glyph: '❌', label: 'Outmatched',   color: '#f87171' },
    unknown: { glyph: '',   label: '',             color: '#9ca3af' },
};

export function fitVerdict(chance) {
    if (chance >= FIT_GOOD) return 'good';
    if (chance >= FIT_CAUTION) return 'caution';
    return 'poor';
}

/**
 * How the two halves combine.
 *
 * LEVEL DOMINATES, deliberately. An even split lets full coverage floor the
 * score at 0.5, so a character with no skills at all could never read
 * "outmatched" in their own faction's best region — the one place they most
 * need telling. Coverage still ranks regions against each other at any given
 * skill level; it just cannot carry a player who cannot pass their own checks.
 *
 * Provisional, like every other L3 number: S7 calibrates.
 */
const COVERAGE_WEIGHT = 0.35;
const LEVEL_WEIGHT = 0.65;

/**
 * Share of a region's primary checks that belong to `faction`, weighted by how
 * often each room type is actually met.
 */
export function factionCoverage(regionKey, faction) {
    const region = config.raidRegions?.[regionKey];
    if (!region || !faction) return 0;

    let own = 0, total = 0;
    for (const [list, perRaid] of [[region.gates || [], GATES_PER_RAID], [region.hazards || [], HAZARDS_PER_RAID]]) {
        const primaries = list.filter(room => room.primary);
        if (!primaries.length) continue;
        const perRoom = perRaid / primaries.length;
        for (const room of primaries) {
            if (getSkillFaction(room.primary) === faction) own += perRoom;
            total += perRoom;
        }
    }
    return total > 0 ? own / total : 0;
}

/**
 * Best coverage any region offers a faction, so coverage is scored against what
 * is ACHIEVABLE rather than against 100%.
 *
 * Regions draw primaries from every faction — the most dominion-heavy region in
 * the game is only half dominion — so a raw coverage term caps every score near
 * 0.5 and "well matched" becomes unreachable. Scored against the faction's own
 * best region instead, the same trick `demand-core` uses for premiums.
 *
 * Pure over static config, so computed once.
 */
let bestCoverageCache = null;

function bestCoverageFor(faction) {
    if (!bestCoverageCache) {
        bestCoverageCache = {};
        for (const f of Object.keys(config.factionSkills || {})) {
            bestCoverageCache[f] = Math.max(
                0, ...Object.keys(config.raidRegions || {}).map(r => factionCoverage(r, f)));
        }
    }
    return bestCoverageCache[faction] || 0;
}

/**
 * How well this character fits a region at a tier.
 *
 * Two inputs, and only two:
 *
 *   COVERAGE  — the share of the region's primary checks that belong to the
 *               character's OWN faction, scored against the best any region
 *               offers that faction. This is the part of the raid genuinely on
 *               them, and more of it is better: it is the region matching who
 *               they are.
 *   LEVEL FIT — how ready they are for exactly those checks, as success chance.
 *
 * Rooms needing another faction's skills are not scored at all. Those get
 * hired, and at region-selection time no crew exists to measure — judging the
 * player on skills they were never going to bring is measuring the wrong thing.
 *
 * The two are AVERAGED, not blended toward an assumed contractor baseline. That
 * baseline was tried first and inverts at low skill: with off-faction rooms
 * counted at a flat "assume a good hire", a level-3 character scored their far,
 * uncharted region ABOVE their home one — because 92% of it was assumed carried
 * by a crew they cannot afford. Coverage has to push the same direction as
 * level, or the glyph advises new players to leave home.
 *
 * Primaries only. Support skills apply a fractional bonus rather than gating a
 * room, so folding them in would dilute exactly the signal this is for: the
 * primary is the check you must actually pass to get through the door.
 *
 * @returns {{score:number, verdict:string, coverage:number, levelFit:number,
 *            weakest:{skillId,chance}|null}|null}
 *   null when there is nothing to judge (no character, unknown region/tier).
 */
export function regionSkillFit(regionKey, tier, character = gameState.character) {
    const region = config.raidRegions?.[regionKey];
    const tierData = config.raidTierData?.[tier];
    if (!region || !tierData || !character?.faction) return null;

    const ownFaction = character.faction;
    const rooms = [
        { list: region.gates || [], target: tierData.gateSkillLevel, perRaid: GATES_PER_RAID },
        { list: region.hazards || [], target: tierData.hazardSkillLevel, perRaid: HAZARDS_PER_RAID },
    ];

    let ownTotal = 0, ownWeight = 0;
    let weakest = null;

    for (const { list, target, perRaid } of rooms) {
        const primaries = list.filter(room => room.primary);
        if (!primaries.length) continue;
        const perRoom = perRaid / primaries.length;

        for (const room of primaries) {
            const skillId = room.primary;
            if (getSkillFaction(skillId) !== ownFaction) continue;   // hired, not judged

            const level = character.skills?.[skillId] || 0;
            // An unlearned skill is a genuine zero, not a low roll — the check
            // cannot be passed on merit at all. Short-circuited rather than run
            // through the formula, whose lower clamp would flatter it to 20%.
            const chance = level > 0
                ? Math.min(0.97, getSkillCheckChance(level, target) + getSkillArtifactBonus(skillId, character) / 100)
                : 0;

            ownTotal += chance * perRoom;
            ownWeight += perRoom;
            // Only own-faction skills can be a weakness worth naming: an
            // off-faction gap is a hiring note, not something to train.
            if (!weakest || chance < weakest.chance) weakest = { skillId, chance };
        }
    }

    const coverage = factionCoverage(regionKey, ownFaction);
    const best = bestCoverageFor(ownFaction);
    const coverageScore = best > 0 ? Math.min(1, coverage / best) : 0;
    const levelFit = ownWeight > 0 ? ownTotal / ownWeight : 0;
    const score = COVERAGE_WEIGHT * coverageScore + LEVEL_WEIGHT * levelFit;

    return { score, verdict: fitVerdict(score), coverage, coverageScore, levelFit, weakest };
}
